import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type RfiRow, type CoRow, type EstimateRow, type QcReview, type UnitPrice, type ScopeCompleteness } from '../api/client';
import { exportInternalBom, exportRfiLog, exportCoLog, downloadBlob } from '../lib/exports';
import { ProjectSteps } from '../components/ProjectSteps';
import { CloseoutCard } from './CloseoutCard';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

const money = (n: number | null | undefined) => n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

// Phase 7 — outputs. Supplier Pricing PDF (external-safe), Full Internal BOM,
// RFIs, potential change orders, and the Estimate Summary.
export function Outputs() {
  const { t } = useLang();
  const { id = '' } = useParams();
  const [rfis, setRfis] = useState<RfiRow[]>([]);
  const [cos, setCos] = useState<CoRow[]>([]);
  const [estimate, setEstimate] = useState<EstimateRow | null>(null);
  const [qc, setQc] = useState<QcReview | null>(null);
  const [scope, setScope] = useState<ScopeCompleteness | null>(null);
  const [unitPrices, setUnitPrices] = useState<UnitPrice[]>([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  async function load() {
    try {
      const [r, c, e, q, u, sc] = await Promise.all([api.getRfis(id), api.getChangeOrders(id), api.getEstimate(id), api.getQcReview(id), api.getUnitPrices(id).catch(() => ({ unit_prices: [] })), api.getScopeCompleteness(id).catch(() => null)]);
      setRfis(r.rfis); setCos(c.change_orders); setEstimate(e.estimate); setQc(q); setUnitPrices(u.unit_prices); setScope(sc);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
  }
  useEffect(() => { void load(); }, [id]);

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(label); setError('');
    try { await fn(); await load(); } catch (e) { setError(e instanceof Error ? e.message : `${label} failed`); }
    finally { setBusy(''); }
  }

  async function exportBom() {
    setBusy('Building internal BOM…'); setError('');
    try {
      const b = await api.getBom(id);
      if (!b.lines.length) { setError(t({ en: 'No BOM yet — generate it first.', fil: 'Wala pang BOM — i-generate muna ito.' })); return; }
      downloadBlob(await exportInternalBom(estimate?.costing ? 'Project' : 'Project', b.lines, today()), 'Internal_BOM.xlsx');
    } catch (e) { setError(e instanceof Error ? e.message : 'Export failed'); }
    finally { setBusy(''); }
  }

  return (
    <div>
      <Link to={`/projects/${id}/count`} className="muted" style={{ fontSize: 13 }}>← {t({ en: 'Count Sheet', fil: 'Count Sheet' })}</Link>
      <h1 style={{ margin: '4px 0 0' }}>Bid &amp; Proposal</h1>
      <div style={{ marginTop: 12 }}><ProjectSteps current="bid" /></div>
      <div className="card" style={{ marginTop: 12, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div><strong>{t({ en: 'Adjust pricing before you send.', fil: 'I-adjust ang pricing bago mo ipadala.' })}</strong> <span className="muted" style={{ fontSize: 13 }}>{t({ en: 'Wage type & county (prevailing wage / Davis-Bacon), tax, markups, margin %, and travel — then regenerate the bid.', fil: 'Wage type & county (prevailing wage / Davis-Bacon), tax, markups, margin %, at travel — pagkatapos i-regenerate ang bid.' })}</span></div>
        <Link className="btn btn-primary btn-sm" to={`/projects/${id}/costing`} style={{ whiteSpace: 'nowrap' }}>{t({ en: 'Pricing, labor & margin →', fil: 'Pricing, labor & margin →' })}</Link>
      </div>
      <p className="muted" style={{ marginTop: 12 }}>{t({ en: 'Your estimate, proposal PDFs, supplier RFQ and internal BOM. Internal documents are never sent to customers.', fil: 'Ang iyong estimate, proposal PDFs, supplier RFQ at internal BOM. Ang internal documents ay hindi kailanman ipinapadala sa mga customer.' })}</p>
      {error && <div className="error">{error}</div>}

      {/* Estimate + documents */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2>{t({ en: 'Estimate & documents', fil: 'Estimate & mga dokumento' })}</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn btn-primary" disabled={!!busy} onClick={() => run('Saving estimate…', () => api.saveEstimate(id))}>
            {busy === 'Saving estimate…' ? busy : `${t(G.save)} Estimate`}
          </button>
          <a className="btn btn-ghost" href={api.pdfUrl(id, 'supplier-pdf')}>Supplier Pricing PDF (Button 1)</a>
          <button className="btn btn-ghost" disabled={!!busy} onClick={exportBom}>Full Internal BOM (Button 2)</button>
          <a className="btn btn-ghost" href={api.pdfUrl(id, 'estimate-pdf')}>Internal Estimate PDF</a>
        </div>
        {estimate && (
          <div className="banner" style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{t({ en: 'Estimate', fil: 'Estimate' })} v{estimate.version} {t({ en: 'saved', fil: 'na-save noong' })} {estimate.created_at}.</span>
            <strong style={{ fontSize: 18, color: 'var(--teal-dark)' }}>{money(estimate.final_sell_price)}</strong>
          </div>
        )}
        <p className="muted" style={{ marginBottom: 0, fontSize: 12 }}>
          {t({ en: 'The Supplier PDF lists material quantities only — no labor, pricing, markup or bid total. The internal documents are confidential.', fil: 'Ang Supplier PDF ay naglilista lamang ng material quantities — walang labor, pricing, markup o bid total. Confidential ang internal documents.' })}
        </p>
      </div>

      {/* Proposals — final number only */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Proposals <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— final number only; no internal breakdown', fil: '— final number lamang; walang internal breakdown' })}</span></h2>
        {!estimate && <p className="error">{t({ en: 'Save an estimate first — the proposal pulls the investment total and scope from the estimate.', fil: 'I-save muna ang estimate — kinukuha ng proposal ang investment total at scope mula sa estimate.' })}</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <a className="btn btn-gold" href={api.pdfUrl(id, 'proposal-word')}>📄 {t(G.download)} Word Proposal (.doc)</a>
          <span className="muted" style={{ fontSize: 12 }}>{t({ en: '— full house-format document: cover, scope & bill of materials, investment summary, terms, acceptance. Opens in Microsoft Word.', fil: '— full house-format document: cover, scope & bill of materials, investment summary, terms, acceptance. Bubukas sa Microsoft Word.' })}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          <a className="btn btn-ghost btn-sm" href={api.pdfUrl(id, 'proposal-short-pdf')}>Short Proposal (PDF)</a>
          <a className="btn btn-ghost btn-sm" href={api.pdfUrl(id, 'proposal-full-pdf')}>Full Proposal (PDF)</a>
        </div>
        <p className="muted" style={{ marginBottom: 0, fontSize: 12, marginTop: 8 }}>
          {t({ en: 'Company content comes from the editable Company Profile (Admin) — the software invents no company facts. The proposal shows material quantities and a category-level investment summary; it never exposes unit pricing, markup or profit.', fil: 'Ang company content ay galing sa editable na Company Profile (Admin) — walang ini-imbentong company facts ang software. Ipinapakita ng proposal ang material quantities at category-level na investment summary; hindi nito kailanman inilalantad ang unit pricing, markup o profit.' })}
        </p>
      </div>

      {/* QC bid review */}
      {qc && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>{t({ en: 'QC bid review', fil: 'QC bid review' })}</h2>
            <div className="muted">{qc.summary.pass} {t({ en: 'pass', fil: 'pasado' })} · <span style={{ color: 'var(--warning)' }}>{qc.summary.warn} {t({ en: 'warn', fil: 'babala' })}</span> · <span style={{ color: 'var(--danger)' }}>{qc.summary.fail} {t({ en: 'fail', fil: 'bagsak' })}</span></div>
          </div>
          <table style={{ marginTop: 8 }}>
            <tbody>
              {qc.checks.map((ch) => (
                <tr key={ch.key}>
                  <td style={{ width: 24 }}>{ch.status === 'pass' ? '✅' : ch.status === 'warn' ? '⚠️' : '❌'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}><strong>{ch.label}</strong></td>
                  <td className="muted">{ch.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Scope completeness (flywheel #6) — catch forgotten scope before submitting */}
      {scope && scope.items.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>{t({ en: 'Scope completeness', fil: 'Scope completeness' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— the #1 way to lose money is forgetting scope', fil: '— ang #1 dahilan ng pagkalugi ay ang nakalimutang scope' })}</span></h2>
            <div className="muted">
              {scope.summary.covered} {t({ en: 'covered', fil: 'sakop' })} · <span style={{ color: 'var(--warning)' }}>{scope.summary.verify} {t({ en: 'verify', fil: 'i-verify' })}</span> · <span style={{ color: 'var(--danger)' }}>{scope.summary.missing} {t({ en: 'likely missing', fil: 'malamang kulang' })}</span>
            </div>
          </div>
          {scope.summary.missing > 0 && <div className="banner banner-danger" style={{ marginTop: 8, fontSize: 13 }}>⚠️ {t({ en: `${scope.summary.missing} item(s) usually required are not in this bid — review before you submit.`, fil: `${scope.summary.missing} item(s) na karaniwang kailangan ay wala sa bid na ito — i-review bago mo isumite.` })}</div>}
          <table style={{ marginTop: 8 }}>
            <tbody>
              {[...scope.items].sort((a, b) => ({ missing: 0, verify: 1, covered: 2 }[a.status] - { missing: 0, verify: 1, covered: 2 }[b.status])).map((it) => (
                <tr key={it.id}>
                  <td style={{ width: 24 }}>{it.status === 'covered' ? '✅' : it.status === 'verify' ? '⚠️' : '❌'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}><strong>{it.label}</strong><div className="muted" style={{ fontSize: 11 }}>{it.group}</div></td>
                  <td className="muted">{it.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Job closeout — estimate vs actual (flywheel #3) */}
      {id && <CloseoutCard projectId={id} />}

      {/* RFIs */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>RFIs <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>({rfis.length})</span></h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => run('Generating RFIs…', () => api.generateRfis(id))}>{t({ en: 'Generate RFIs', fil: 'Mag-generate ng RFIs' })}</button>
            <button className="btn btn-ghost btn-sm" disabled={!rfis.length} onClick={async () => downloadBlob(await exportRfiLog('Project', rfis, today()), 'RFI_Log.xlsx')}>{t({ en: 'Export', fil: 'I-export' })}</button>
          </div>
        </div>
        {!rfis.length && <p className="muted">{t({ en: 'No RFIs yet. Generate from the analysis flags.', fil: 'Wala pang RFIs. Mag-generate mula sa analysis flags.' })}</p>}
        {rfis.map((r) => (
          <div key={r.id} style={{ borderLeft: '3px solid var(--gold)', padding: '6px 10px', marginTop: 8 }}>
            <div><strong>{r.title}</strong></div>
            <div>{r.question}</div>
            <div className="muted" style={{ fontSize: 12 }}>{r.reason} · {r.source_ref} · {r.cost_schedule_impact}</div>
          </div>
        ))}
      </div>

      {/* Change orders */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>{t({ en: 'Potential change orders', fil: 'Mga posibleng change order' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>({cos.length})</span></h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => run('Generating change orders…', () => api.generateChangeOrders(id))}>{t({ en: 'Generate', fil: 'Mag-generate' })}</button>
            <button className="btn btn-ghost btn-sm" disabled={!cos.length} onClick={async () => downloadBlob(await exportCoLog('Project', cos, today()), 'Change_Order_Log.xlsx')}>{t({ en: 'Export', fil: 'I-export' })}</button>
          </div>
        </div>
        {!cos.length && <p className="muted">{t({ en: 'No change-order risks yet. Generate from alternates, allowances and scope flags.', fil: 'Wala pang change-order risks. Mag-generate mula sa alternates, allowances at scope flags.' })}</p>}
        {cos.map((c) => (
          <div key={c.id} style={{ borderLeft: `3px solid ${c.risk_level === 'high' ? 'var(--danger)' : 'var(--gold)'}`, padding: '6px 10px', marginTop: 8 }}>
            <div><strong>{c.title}</strong> <span className={`pill pill-${c.risk_level === 'high' ? 'needs_review' : 'proposed'}`}>{c.risk_level}</span></div>
            <div>{c.description}</div>
            <div className="muted" style={{ fontSize: 12 }}>{c.why} · {c.source_ref} · {c.recommendation}</div>
          </div>
        ))}
      </div>

      {/* Unit-price / add-alternate book */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>{t({ en: 'Unit prices — adds / deducts', fil: 'Unit prices — adds / deducts' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>({unitPrices.length} {t({ en: `device types · fully burdened at this job's markups & wage`, fil: 'device types · fully burdened sa markups & wage ng job na ito' })})</span></h2>
          <button className="btn btn-ghost btn-sm" disabled={!unitPrices.length} onClick={() => {
            const head = 'Device,System,Material,Labor hrs,Labor cost,Unit sell price\n';
            const rows = unitPrices.map((u) => `"${u.label}","${u.system}",${u.unit_material},${u.unit_labor_hours},${u.unit_labor_cost},${u.unit_sell}`).join('\n');
            downloadBlob(new Blob([head + rows], { type: 'text/csv' }), 'Unit_Price_Book.csv');
          }}>{t({ en: 'Export CSV', fil: 'I-export ang CSV' })}</button>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>{t({ en: `Per-device installed sell price for change orders and add/deduct alternates — standard cable allowances, this project's markups, wage basis and productivity factor. Re-price the bid to refresh.`, fil: 'Per-device na installed sell price para sa change orders at add/deduct alternates — standard cable allowances, markups ng project na ito, wage basis at productivity factor. I-re-price ang bid para mag-refresh.' })}</p>
        {!unitPrices.length && <p className="muted">{t({ en: 'No confirmed devices yet — confirm scope and generate the BOM.', fil: 'Wala pang confirmed devices — i-confirm ang scope at i-generate ang BOM.' })}</p>}
        {unitPrices.length > 0 && (
          <table>
            <thead><tr><th>Device</th><th>System</th><th>Material</th><th>Labor hrs</th><th>Labor</th><th>Unit sell</th></tr></thead>
            <tbody>
              {unitPrices.map((u) => (
                <tr key={u.device_key}>
                  <td>{u.label}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{u.system}</td>
                  <td>{money(u.unit_material)}</td>
                  <td>{u.unit_labor_hours.toFixed(2)}</td>
                  <td>{money(u.unit_labor_cost)}</td>
                  <td><strong>{money(u.unit_sell)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
